import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";

// Types
export interface ApiQuota {
  id: string;
  service: string;
  monthly_limit: number | null;
  daily_limit: number | null;
  hourly_limit: number | null;
  warning_threshold: number;
  cost_per_unit: number;
  cost_currency: string;
  retention_days: number;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApiUsageLog {
  id: string;
  service: string;
  operation: string;
  function_name: string;
  status: "pending" | "success" | "failed" | "rate_limited";
  cost_estimate: number | null;
  metadata: Record<string, unknown> | null;
  created_by: string | null;
  created_at: string;
}

export interface UsageSummary {
  service: string;
  hourly_count: number;
  daily_count: number;
  monthly_count: number;
  hourly_limit: number | null;
  daily_limit: number | null;
  monthly_limit: number | null;
  monthly_cost: number;
  rate_limited_count: number;
  warning_threshold: number;
}

export interface DailyUsage {
  date: string;
  service: string;
  count: number;
  cost: number;
}

/**
 * Fetch API quotas
 */
export function useApiQuotas() {
  return useQuery({
    queryKey: queryKeys.apiUsage.quotas,
    queryFn: async (): Promise<ApiQuota[]> => {
      const { data, error } = await supabase
        .from("api_quotas")
        .select("*")
        .order("service");

      if (error) throw error;
      return data || [];
    },
  });
}

/**
 * Fetch usage summary (current period counts)
 */
export function useApiUsageSummary() {
  return useQuery({
    queryKey: queryKeys.apiUsage.summary,
    queryFn: async (): Promise<UsageSummary[]> => {
      const { data, error } = await supabase.rpc("get_api_usage_summary");
      if (error) throw error;
      return data || [];
    },
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Fetch usage logs with pagination
 */
export function useApiUsageLogs(options?: {
  service?: string;
  status?: string;
  limit?: number;
  offset?: number;
}) {
  const { service, status, limit = 50, offset = 0 } = options || {};

  return useQuery({
    queryKey: queryKeys.apiUsage.logsWithParams(service, status, limit, offset),
    queryFn: async () => {
      let query = supabase
        .from("api_usage_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (service) {
        query = query.eq("service", service);
      }
      if (status) {
        query = query.eq("status", status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });
}

/**
 * Fetch daily usage for charts (last 30 days)
 */
export function useDailyUsage(days = 30) {
  return useQuery({
    queryKey: queryKeys.apiUsage.dailyUsageWithDays(days),
    queryFn: async (): Promise<DailyUsage[]> => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from("api_usage_logs")
        .select("service, created_at, cost_estimate")
        .eq("status", "success")
        .gte("created_at", startDate.toISOString());

      if (error) throw error;

      // Group by date and service
      const grouped: Record<string, DailyUsage> = {};

      (data || []).forEach((log) => {
        const date = log.created_at.split("T")[0];
        const key = `${date}-${log.service}`;

        if (!grouped[key]) {
          grouped[key] = {
            date,
            service: log.service,
            count: 0,
            cost: 0,
          };
        }
        grouped[key].count += 1;
        grouped[key].cost += log.cost_estimate || 0;
      });

      return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
    },
  });
}

/**
 * Update quota settings
 */
export function useUpdateQuota() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quota: Partial<ApiQuota> & { id: string }) => {
      const { id, ...updates } = quota;
      const { error } = await supabase
        .from("api_quotas")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiUsage.quotas });
      queryClient.invalidateQueries({ queryKey: queryKeys.apiUsage.summary });
    },
  });
}

/**
 * Delete old logs manually (admin action)
 */
export function useCleanupLogs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("cleanup_old_api_logs");
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiUsage.logs });
    },
  });
}

/**
 * Get quota warnings (services near limit)
 */
export function useQuotaWarnings() {
  const { data: summary } = useApiUsageSummary();

  if (!summary) return [];

  return summary
    .filter((s) => {
      if (!s.monthly_limit) return false;
      const usage = s.monthly_count / s.monthly_limit;
      return usage >= s.warning_threshold;
    })
    .map((s) => ({
      service: s.service,
      used: s.monthly_count,
      limit: s.monthly_limit!,
      percentage: Math.round((s.monthly_count / s.monthly_limit!) * 100),
      level: s.monthly_count >= s.monthly_limit! ? "critical" : 
             s.monthly_count / s.monthly_limit! >= 0.9 ? "high" : "warning",
    }));
}
